import {
  useEffect,
  useState,
} from "react";
import {
  Link,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  ArrowLeft,
  Briefcase,
  Globe,
  MapPin,
} from "lucide-react";
import { api } from "../lib/api";
import EmployerLogo from "../components/EmployerLogo";
import JobCard from "../components/JobCard";
import NewsletterCTA from "../components/NewsletterCTA";

export default function OperatorDetail() {
  const { slug } =
    useParams();

  const navigate =
    useNavigate();

  const [op, setOp] =
    useState(null);

  const [
    vacancies,
    setVacancies,
  ] = useState([]);

  const [
    notFound,
    setNotFound,
  ] = useState(false);

  const [
    loading,
    setLoading,
  ] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadOperator =
      async () => {
        setLoading(true);
        setNotFound(false);
        setOp(null);
        setVacancies([]);

        /*
         * A registered, approved employer with a published company page
         * should use the richer /companies/:slug page.
         */
        try {
          await api.get(
            `/companies/${slug}`
          );

          if (!cancelled) {
            navigate(
              `/companies/${slug}`,
              {
                replace: true,
              }
            );
          }

          return;
        } catch {
          // No published company page: use the generic operator fallback.
        }

        try {
          const response =
            await api.get(
              `/operators/${slug}`
            );

          if (cancelled) {
            return;
          }

          const operator =
            response.data;

          setOp(operator);

          const vacancyResponse =
            await api.get(
              `/vacancies?employer=${encodeURIComponent(
                operator.name
              )}`
            );

          if (cancelled) {
            return;
          }

          const liveVacancies =
            Array.isArray(
              vacancyResponse.data
            )
              ? vacancyResponse.data
              : [];

          setVacancies(
            liveVacancies
          );

          document.title =
            `${operator.name} careers — ${operator.count} live UK rail vacancies | Railway Careers UK`;

          const meta =
            document.querySelector(
              'meta[name="description"]'
            ) ||
            (() => {
              const element =
                document.createElement(
                  "meta"
                );

              element.setAttribute(
                "name",
                "description"
              );

              document.head.appendChild(
                element
              );

              return element;
            })();

          meta.setAttribute(
            "content",
            `Browse every live ${operator.name} vacancy on Railway Careers UK. Application links go directly to the employer's own careers site — no recruiters and no application fees.`
          );
        } catch {
          if (!cancelled) {
            setNotFound(true);
          }
        } finally {
          if (!cancelled) {
            setLoading(false);
          }
        }
      };

    loadOperator();

    return () => {
      cancelled = true;
    };
  }, [
    slug,
    navigate,
  ]);

  if (notFound) {
    return (
      <div className="bg-nav min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <div className="font-display text-ice text-3xl font-bold mb-3">
            Employer not found
          </div>

          <p className="text-slate mb-6">
            No public employer profile or live vacancies are currently
            available for this organisation.
          </p>

          <Link
            to="/operators"
            className="pill pill-primary"
          >
            All rail employers
          </Link>
        </div>
      </div>
    );
  }

  if (
    loading ||
    !op
  ) {
    return (
      <div className="bg-nav min-h-screen p-12 text-center text-slate">
        Loading employer…
      </div>
    );
  }

  const featured =
    vacancies.filter(
      (vacancy) =>
        vacancy.featured
    );

  const regular =
    vacancies.filter(
      (vacancy) =>
        !vacancy.featured
    );

  const regions =
    Array.from(
      new Set(
        vacancies
          .map(
            (vacancy) =>
              vacancy.region
          )
          .filter(Boolean)
      )
    );

  return (
    <div className="bg-nav min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-12 pb-10">
        <Link
          to="/operators"
          className="inline-flex items-center gap-2 text-[13px] text-slate hover:text-sky transition-colors"
          data-testid="operator-back"
        >
          <ArrowLeft className="w-4 h-4" />
          All rail employers
        </Link>

        <div className="mt-8 flex items-start gap-6 flex-wrap">
          <EmployerLogo
            name={op.name}
            size={96}
            className="!rounded-xl shrink-0"
          />

          <div className="flex-1 min-w-0">
            <div className="chapter-mark">
              UK Rail Employer
            </div>

            <h1 className="mt-3 font-display text-ice text-4xl md:text-6xl tracking-[-0.025em] font-extrabold leading-[1.05]">
              {op.name}{" "}
              <span className="text-sky">
                careers.
              </span>
            </h1>

            <p className="mt-4 text-slate text-lg max-w-3xl leading-relaxed">
              Every live {op.name} vacancy on Railway Careers UK. Each role
              links directly to {op.name}'s own application page — no
              recruiters, no application fees and no middlemen.
            </p>

            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-slate font-mono">
              <span className="inline-flex items-center gap-1.5 text-sky">
                <Briefcase className="w-4 h-4" />
                {op.count} live{" "}
                {op.count === 1
                  ? "vacancy"
                  : "vacancies"}
              </span>

              {regions.length >
                0 && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-sky" />
                  {regions
                    .slice(
                      0,
                      4
                    )
                    .join(
                      " · "
                    )}
                </span>
              )}

              <Link
                to={`/vacancies?employer=${encodeURIComponent(
                  op.name
                )}`}
                className="inline-flex items-center gap-1.5 text-sky hover:text-ice"
              >
                <Globe className="w-4 h-4" />
                Filter vacancies →
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 pb-20">
        {featured.length >
          0 && (
          <div className="mb-10">
            <div className="text-[11px] uppercase tracking-[0.18em] text-sky font-mono mb-4">
              Featured roles
            </div>

            <div
              className="grid md:grid-cols-2 gap-5"
              data-testid="operator-featured-grid"
            >
              {featured.map(
                (
                  vacancy
                ) => (
                  <JobCard
                    key={
                      vacancy.id
                    }
                    vacancy={
                      vacancy
                    }
                  />
                )
              )}
            </div>
          </div>
        )}

        {regular.length >
          0 && (
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate font-mono mb-4">
              All live vacancies
            </div>

            <div
              className="grid md:grid-cols-2 gap-5"
              data-testid="operator-vacancies-grid"
            >
              {regular.map(
                (
                  vacancy
                ) => (
                  <JobCard
                    key={
                      vacancy.id
                    }
                    vacancy={
                      vacancy
                    }
                  />
                )
              )}
            </div>
          </div>
        )}

        {vacancies.length ===
          0 && (
          <div className="paper-card p-10 text-center text-slate">
            No live vacancies right now. Subscribe to the Monday Briefing
            and we'll let you know when {op.name} appears again.
          </div>
        )}

        <div className="mt-14">
          <div className="text-[11px] uppercase tracking-[0.18em] text-sky font-mono mb-4">
            Common roles at {op.name}
          </div>

          <div
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3"
            data-testid="operator-role-links"
          >
            {[
              {
                slug: "train-driver",
                label: "Train driver",
              },
              {
                slug: "conductor",
                label: "Conductor / Train manager",
              },
              {
                slug: "depot-driver",
                label: "Depot driver",
              },
              {
                slug: "fitter",
                label: "Fitter / Engineer",
              },
              {
                slug: "customer-service",
                label: "Station / Customer service",
              },
              {
                slug: "engineering",
                label: "Signalling & Engineering",
              },
              {
                slug: "graduate",
                label: "Graduate scheme",
              },
              {
                slug: "apprentice",
                label: "Apprenticeship",
              },
            ].map(
              (
                role
              ) => (
                <Link
                  key={
                    role.slug
                  }
                  to={`/careers/${role.slug}`}
                  className="block bg-nav-light/30 border border-line hover:border-sky rounded-lg p-3 transition-colors"
                >
                  <div className="text-[13px] font-semibold text-ice">
                    {
                      role.label
                    }
                  </div>

                  <div className="text-[11px] text-slate mt-0.5">
                    {op.name} · UK rail role guide
                  </div>
                </Link>
              )
            )}
          </div>

          <div className="mt-5">
            <Link
              to="/train-driver-jobs"
              className="text-sky hover:text-ice text-sm font-semibold inline-flex items-center gap-1.5"
            >
              Read the full UK Train Driver Jobs guide → £27k–£68k,
              training, assessments
            </Link>
          </div>
        </div>
      </div>

      <NewsletterCTA />
    </div>
  );
}